import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { C, card, label, btn, input, SIGNS, GLYPH, PLANET_GLYPH, ELEMENT_COLOR } from "../lib/theme";

const CATS = [["astrology", "Astrology Related"], ["suggestions", "Suggestions & Bugs"],
              ["off_topic", "Non-Astrology"]];
const TAG_BODIES = ["Sun","Moon","Mercury","Venus","Mars","Jupiter","Saturn",
  "Uranus","Neptune","Pluto","Rising"];
const TAG_HOUSES = ["1st","2nd","3rd","4th","5th","6th","7th","8th","9th","10th",
  "11th","12th"].map((n) => `${n} house`);
const TAG_GROUPS = [["Bodies", TAG_BODIES], ["Signs", SIGNS], ["Houses", TAG_HOUSES]];
const MAX_TAGS = 6;

function tagColor(t) {
  if (SIGNS.includes(t)) return ELEMENT_COLOR(t);
  return C.brass;
}
function tagGlyph(t) {
  if (SIGNS.includes(t)) return GLYPH[t] + " ";
  if (PLANET_GLYPH[t]) return PLANET_GLYPH[t] + " ";
  if (t === "Rising") return "↑ ";
  return "";
}

function TagChip({ t, active, onClick, small }) {
  const c = tagColor(t);
  return (
    <button onClick={onClick} disabled={!onClick}
      style={{ background: active ? c : "transparent", color: active ? C.bg : c,
        border: `1px solid ${c}`, borderRadius: 999, cursor: onClick ? "pointer" : "default",
        padding: small ? "1px 8px" : "3px 10px", fontSize: small ? 10 : 11.5,
        fontFamily: "inherit", opacity: active || !small ? 1 : 0.8 }}>
      {tagGlyph(t)}{t}</button>
  );
}

function Author({ p }) {
  return (
    <span style={{ fontSize: 11.5, color: C.dim, display: "inline-flex", gap: 6, alignItems: "center" }}>
      <Link to={`/u/${encodeURIComponent(p.author)}`} onClick={(e) => e.stopPropagation()}
        style={{ color: C.dim, textDecoration: "none", borderBottom: `1px dotted ${C.faint}` }}>
        {p.author}</Link>
      {p.author_title && <span style={{ fontSize: 9.5, letterSpacing: "0.08em",
        color: p.author_title_kind === "custom" ? C.brass : C.faint,
        border: `1px solid ${p.author_title_kind === "custom" ? C.brass : C.line}`,
        borderRadius: 3, padding: "1px 5px" }}>
        {p.author_title_kind === "custom" && "✶ "}{p.author_title.toUpperCase()}</span>}
      {p.author_signature && <a href={p.author_signature} target="_blank" rel="noreferrer"
        style={{ color: C.brass, textDecoration: "none" }}>
        {p.author_signature.replace("https://", "")}</a>}
    </span>
  );
}

export default function Board({ me }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [cat, setCat] = useState("astrology");
  const [posts, setPosts] = useState([]);
  const [detail, setDetail] = useState(null);
  const [compose, setCompose] = useState(false);
  const [title, setTitle] = useState(""); const [body, setBody] = useState("");
  const [reply, setReply] = useState(""); const [err, setErr] = useState("");
  const [postTags, setPostTags] = useState([]);       // tags on the post being composed
  const [reported, setReported] = useState({});       // postId -> true after reporting
  const [filterTags, setFilterTags] = useState([]);   // sidebar filter selection
  const [filterOpen, setFilterOpen] = useState(false);

  const toggleIn = (list, setList, t, max) => setList(
    list.includes(t) ? list.filter((x) => x !== t)
      : max && list.length >= max ? list : [...list, t]);

  const loadList = () => api.board(cat, filterTags).then(setPosts);
  useEffect(() => { if (!id) { setDetail(null); loadList(); } }, [cat, id, filterTags.join()]);
  useEffect(() => { if (id) api.post(id).then(setDetail); }, [id]);

  const act = (fn) => fn().then(() => (id ? api.post(id).then(setDetail) : loadList()))
    .catch((e) => setErr(e.message));

  if (id && detail) return (
    <div style={{ maxWidth: 760 }}>
      <button onClick={() => nav("/board")} style={{ all: "unset", cursor: "pointer",
        color: C.brass, fontSize: 12.5, marginBottom: 10 }}>← All posts</button>
      <div style={{ ...card, marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 9.5, letterSpacing: "0.12em", color: C.faint }}>
            {detail.category?.toUpperCase()}</span>
          {detail.pinned && <span style={{ fontSize: 9, letterSpacing: "0.1em",
            color: C.brass, border: `1px solid ${C.brass}`, borderRadius: 3,
            padding: "1px 5px" }}>PINNED</span>}
          {detail.locked && <span style={{ fontSize: 9, letterSpacing: "0.1em",
            color: C.faint, border: `1px solid ${C.line}`, borderRadius: 3,
            padding: "1px 5px" }}>🔒 LOCKED</span>}
          {(detail.tags || []).map((t) => <TagChip key={t} t={t} small />)}
        </div>
        <div style={{ fontFamily: C.displayFont, fontSize: 23, margin: "4px 0 8px" }}>
          {detail.title}</div>
        <div style={{ display: "flex", gap: 12, alignItems: "baseline", marginBottom: 10 }}>
          <Author p={detail} />
          <span style={{ marginLeft: "auto", fontSize: 11.5, color: C.brass }}>
            ◆ {detail.worth_reading}</span>
          <button onClick={() => act(() => api.vote(detail.id))}
            style={{ ...btn(false), padding: "3px 10px", fontSize: 11 }}>worth reading</button>
        </div>
        {detail.body.split("\n\n").map((t, i) =>
          <p key={i} style={{ fontSize: 13.5, lineHeight: 1.7, margin: "0 0 10px" }}>{t}</p>)}
        <div style={{ display: "flex", gap: 14, alignItems: "baseline" }}>
          {reported[detail.id]
            ? <span style={{ color: C.brass, fontSize: 11 }}>✓ reported — a moderator will review</span>
            : <button onClick={() => api.report(detail.id, "reported from post view")
                  .then(() => setReported((r) => ({ ...r, [detail.id]: true })))
                  .catch((e) => setErr(e.message))}
                style={{ all: "unset", cursor: "pointer", color: C.faint, fontSize: 11 }}>
                report</button>}
          {me?.is_moderator && <>
            <button onClick={() => act(() => detail.pinned
                ? api.modUnpin(detail.id) : api.modPin(detail.id))}
              style={{ all: "unset", cursor: "pointer", color: C.brass, fontSize: 11,
                marginLeft: "auto" }}>
              {detail.pinned ? "unpin" : "📌 pin"}</button>
            <button onClick={() => act(() => detail.locked
                ? api.modUnlock(detail.id) : api.modLock(detail.id))}
              style={{ all: "unset", cursor: "pointer", color: C.brass, fontSize: 11 }}>
              {detail.locked ? "unlock" : "🔒 lock"}</button>
          </>}
        </div>
      </div>
      <div style={label}>REPLIES</div>
      {detail.replies.map((r) => (
        <div key={r.id} style={{ ...card, padding: "12px 16px", marginBottom: 8 }}>
          <div style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 6 }}>{r.body}</div>
          <Author p={r} />
        </div>
      ))}
      {detail.locked && !me?.is_moderator ? (
        <div style={{ ...card, padding: "12px 16px", fontSize: 12.5, color: C.faint }}>
          🔒 This topic is locked — it carries curated updates from the site. Discussion
          happens in the open findings thread.
        </div>
      ) : (
        <div style={{ ...card, padding: "12px 16px" }}>
          <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3}
            placeholder="Reply (plain text)" style={{ ...input, width: "100%", boxSizing: "border-box" }} />
          <button style={{ ...btn(), marginTop: 8 }} onClick={() =>
            act(() => api.createPost({ parent_id: detail.id, body: reply })).then(() => setReply(""))}>
            Reply</button>
        </div>
      )}
      {err && <div style={{ color: C.rose, fontSize: 12, marginTop: 8 }}>{err}</div>}
    </div>
  );

  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start", maxWidth: 980,
      flexWrap: "wrap" }}>
      <div style={{ flex: "1 1 560px", minWidth: 0, maxWidth: 760 }}>
        <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
          {CATS.map(([k, l]) => (
            <button key={k} onClick={() => setCat(k)}
              style={{ background: cat === k ? C.brass : C.surface, color: cat === k ? C.bg : C.dim,
                border: `1px solid ${cat === k ? C.brass : C.line}`, borderRadius: 999,
                padding: "5px 14px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>{l}</button>
          ))}
          <button onClick={() => setCompose(!compose)} style={{ ...btn(), marginLeft: "auto" }}>
            {compose ? "Cancel" : "New post"}</button>
        </div>
        {filterTags.length > 0 && (
          <div style={{ display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap",
            marginBottom: 10, fontSize: 11.5, color: C.dim }}>
            Showing posts tagged
            {filterTags.map((t) => <TagChip key={t} t={t} active small
              onClick={() => toggleIn(filterTags, setFilterTags, t)} />)}
            <button onClick={() => setFilterTags([])} style={{ all: "unset",
              cursor: "pointer", color: C.faint, fontSize: 11 }}>clear</button>
          </div>
        )}
        {compose && (
          <div style={{ ...card, marginBottom: 14 }}>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title"
              style={{ ...input, width: "100%", boxSizing: "border-box", marginBottom: 8 }} />
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5}
              placeholder="Plain text only" style={{ ...input, width: "100%", boxSizing: "border-box" }} />
            <div style={{ fontSize: 10.5, color: C.faint, margin: "10px 0 4px",
              letterSpacing: "0.08em" }}>
              TAGS (OPTIONAL, UP TO {MAX_TAGS}) — help people find this post</div>
            {TAG_GROUPS.map(([g, ts]) => (
              <div key={g} style={{ display: "flex", gap: 4, flexWrap: "wrap",
                marginBottom: 4, alignItems: "center" }}>
                <span style={{ fontSize: 9.5, color: C.faint, width: 44 }}>{g}</span>
                {ts.map((t) => <TagChip key={t} t={t} small active={postTags.includes(t)}
                  onClick={() => toggleIn(postTags, setPostTags, t, MAX_TAGS)} />)}
              </div>
            ))}
            <button style={{ ...btn(), marginTop: 8 }} onClick={() =>
              api.createPost({ title, body, category: cat, tags: postTags })
                .then(() => { setTitle(""); setBody(""); setPostTags([]);
                  setCompose(false); loadList(); })
                .catch((e) => setErr(e.message))}>Post to {CATS.find(([k]) => k === cat)[1]}</button>
          </div>
        )}
        {posts.map((p) => (
          <div key={p.id} style={{ ...card, marginBottom: 10, cursor: "pointer" }}
            onClick={() => nav(`/board/${p.id}`)}>
            <div style={{ fontSize: 15.5, fontWeight: 600 }}>
              {p.pinned && <span style={{ fontSize: 9, letterSpacing: "0.1em", color: C.brass,
                border: `1px solid ${C.brass}`, borderRadius: 3, padding: "1px 5px",
                marginRight: 8 }}>PINNED</span>}
              {p.locked && <span style={{ fontSize: 9, letterSpacing: "0.1em", color: C.faint,
                border: `1px solid ${C.line}`, borderRadius: 3, padding: "1px 5px",
                marginRight: 8 }}>🔒 LOCKED</span>}
              {p.title}
            </div>
            {(p.tags || []).length > 0 && (
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 5 }}
                onClick={(e) => e.stopPropagation()}>
                {p.tags.map((t) => <TagChip key={t} t={t} small
                  active={filterTags.includes(t)}
                  onClick={() => toggleIn(filterTags, setFilterTags, t)} />)}
              </div>
            )}
            <div style={{ fontSize: 12.5, color: C.dim, margin: "5px 0 8px" }}>{p.excerpt}…</div>
            <div style={{ display: "flex", gap: 12, alignItems: "baseline" }}>
              <Author p={p} />
              <span style={{ fontSize: 11, color: C.faint }}>{p.replies} replies</span>
              <span style={{ marginLeft: "auto", fontSize: 11.5, color: C.brass }}>
                ◆ {p.worth_reading}</span>
            </div>
          </div>
        ))}
        {posts.length === 0 && (
          <div style={{ ...card, fontSize: 12.5, color: C.faint }}>
            {filterTags.length ? "No posts match those tags yet — start one!"
              : "No posts here yet."}</div>
        )}
        {err && <div style={{ color: C.rose, fontSize: 12 }}>{err}</div>}
      </div>

      <aside style={{ flex: "0 1 200px", minWidth: 170 }}>
        <div style={{ ...card, padding: "12px 14px" }}>
          <button onClick={() => setFilterOpen(!filterOpen)} style={{ all: "unset",
            cursor: "pointer", width: "100%" }}>
            <div style={{ ...label, marginBottom: filterOpen ? 8 : 0 }}>
              FILTER BY TAG {filterTags.length > 0 && `(${filterTags.length})`}
              <span style={{ float: "right" }}>{filterOpen ? "▴" : "▾"}</span></div>
          </button>
          {filterOpen && TAG_GROUPS.map(([g, ts]) => (
            <div key={g} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 9.5, letterSpacing: "0.1em", color: C.faint,
                marginBottom: 4 }}>{g.toUpperCase()}</div>
              {ts.map((t) => (
                <label key={t} style={{ display: "flex", gap: 6, alignItems: "center",
                  fontSize: 11.5, padding: "1.5px 0", cursor: "pointer",
                  color: filterTags.includes(t) ? C.text : C.dim }}>
                  <input type="checkbox" checked={filterTags.includes(t)}
                    onChange={() => toggleIn(filterTags, setFilterTags, t)}
                    style={{ accentColor: C.brass, margin: 0 }} />
                  <span style={{ color: tagColor(t) }}>{tagGlyph(t) || ""}</span>{t}
                </label>
              ))}
            </div>
          ))}
          {filterOpen && <div style={{ fontSize: 10, color: C.faint }}>
            Shows posts matching any checked tag.</div>}
        </div>
      </aside>
    </div>
  );
}
